/**
 * Status tones mapped to semantic tokens (plans/00 5.7). Words never change:
 * On track, At risk, Needs you. Colour is never the only signal.
 */

import type { DrTone } from '@/lib/dr-types'

export interface ToneStyle {
  /** Text colour. */
  text: string
  /** Soft fill. */
  wash: string
  /** Border. */
  border: string
  /** 3px top border for tiles. */
  accent: string
  /** Solid dot. */
  dot: string
}

const ON_TRACK: ToneStyle = {
  text: 'text-status-on-track',
  wash: 'bg-status-on-track/10',
  border: 'border-status-on-track/30',
  accent: 'border-t-status-on-track',
  dot: 'bg-status-on-track',
}

const AT_RISK: ToneStyle = {
  text: 'text-status-at-risk',
  wash: 'bg-warning-wash',
  border: 'border-status-at-risk/30',
  accent: 'border-t-status-at-risk',
  dot: 'bg-status-at-risk',
}

const NEEDS_YOU: ToneStyle = {
  text: 'text-needs-you',
  wash: 'bg-needs-you-wash',
  border: 'border-needs-you-soft',
  accent: 'border-t-needs-you',
  dot: 'bg-needs-you',
}

const INFO: ToneStyle = {
  text: 'text-foreground',
  wash: 'bg-brand-cornflower/15',
  border: 'border-brand-cornflower/40',
  accent: 'border-t-brand-cornflower',
  dot: 'bg-brand-cornflower',
}

const NEUTRAL: ToneStyle = {
  text: 'text-foreground',
  wash: 'bg-muted',
  border: 'border-border',
  accent: 'border-t-hairline',
  dot: 'bg-muted-foreground',
}

export const TONE_STYLES: Record<DrTone, ToneStyle> = {
  '': NEUTRAL,
  ok: ON_TRACK,
  warn: AT_RISK,
  wait: AT_RISK,
  needs: NEEDS_YOU,
  bad: NEEDS_YOU,
  info: INFO,
}

export function toneStyle(tone: DrTone | null | undefined): ToneStyle {
  return TONE_STYLES[tone ?? ''] ?? NEUTRAL
}

/** The status word for a tone (colour is never the only signal): On track, At risk, Needs you. */
export function toneWord(tone: DrTone | null | undefined): string | null {
  switch (tone) {
    case 'ok':
      return 'On track'
    case 'warn':
    case 'wait':
      return 'At risk'
    case 'needs':
    case 'bad':
      return 'Needs you'
    default:
      return null
  }
}
